import React from 'react'
import { FormikHelpers } from 'formik'
import styled from 'styled-components'

import { Form, Forms, useFormsContext } from '@ally/forms'
import {
  Box,
  Button,
  Label,
  Media,
  TextBody,
  ToggleSwitchStateful,
} from '@ally/metronome-ui'
import { BadWords } from '@ally/use-validation'
import allytm from '@ally/allytm'
import { GuestEnrollCredentialCreationProps } from '@ally/transmitigator'
import { GettingInSvg, LoginHeading } from '@ally/ally-login'

import { CIAMErrorBanner, InputWrapper, SharedNewUsername } from '../Shared'
import { CreatePassword, PasswordHint } from '../Enrollment'
import { useTransmit } from '../../TransmitContext'
import { useAppState } from '../../AppStateProvider'

interface GuestCredentialCreationFormInterface {
  username: string
  password: string
  passwordHint: string
  registerDevice: boolean
}

const StyledTextBody = styled(TextBody)`
  margin-bottom: 24px;
`

const StyledToggleWrapper = styled(Box)`
  display: flex;
  align-items: center;
  .toggle-sub-wrapper {
    display: flex;
    align-items: center;
    #registerDeviceLabelId {
      width: 0;
    }
  }
`

const StyledBox = styled(Box)`
  ${({ theme: { media } }): string => `
    ${media.up('lg')} {
      display: flex;
      justify-content: space-between;
    }
  `}
`

const StyledFormWrapper = styled(Box)`
  ${({ theme: { media } }): string => `
    ${media.up('lg')} {
      width: ${(7 / 12) * 100}%; // based on a 12 column grid
    }
  `}
`

// svg right hand side
const StyledSvgWrapper = styled(Box)`
  width: ${(4 / 12) * 100}%; // based on a 12 column grid
  order: 1;
`

interface GuestCredentialFormProps {
  formId: string
  status: string
}

const GuestCredentialForm: React.FC<GuestCredentialFormProps> = ({
  formId,
  status,
}) => {
  const { setFieldValue } = useFormsContext()
  const { hostServices } = useTransmit()
  const { isWebWrapped } = useAppState()

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Guest Enrollment:Getting In',
    })
  }, [])

  const badWords = hostServices?.hostData.badWords.data
  const badWordsObject: BadWords = badWords || {
    base: [],
    names: [],
    substring: [],
  }

  const handleRegisterDeviceToggle = (toggleValue: boolean): void => {
    setFieldValue('registerDevice', toggleValue)
    const registerDevice = toggleValue ? 'yes' : 'no'
    allytm.event('customEvent', {
      name: 'guestEnrollmentRegisterDevice',
      type: 'userAttribute',
      attr: {
        user: {
          registerDevice,
        },
      },
    })
  }

  return (
    <Form id={formId}>
      <InputWrapper mb="md">
        <SharedNewUsername label="Username" name="username" />
      </InputWrapper>
      <CreatePassword
        title="Password"
        fieldName="password"
        dataTestId="credential-creation-create-password"
      />
      <PasswordHint
        badWords={badWordsObject}
        title="Password hint"
        dataTestId="credential-creation-password-hint"
      />

      {!isWebWrapped && (
        <Box data-testid="register-device-field-wrapper">
          <Box display="block" mb="sm" data-testid="label-wrapper">
            <Label
              id="register-device-label"
              htmlFor="registerDevice"
              data-testid="guest-credential-creation-register-device-label"
            >
              Register device?
            </Label>
          </Box>
          <StyledTextBody
            tag="p"
            size="sm"
            data-testid="guest-credential-creation-register-device"
          >
            If this is a personal device, we can remember it for next time, and
            you won&apos;t need a security code to log in. You should only do
            this for devices you trust, not for shared or public ones.
          </StyledTextBody>

          <StyledToggleWrapper>
            {/* NOTE: this StyledToggleWrapper and extra Label element were added to account for the "No" and "Yes" labels */}
            <Label
              htmlFor="registerDeviceToggleId"
              mb="md"
              mr="lg"
              id="registerDeviceLabelId"
            >
              Register device
            </Label>
            <Box className="toggle-sub-wrapper">
              <Box mb="md" mr="sm">
                <TextBody tag="p" size="md">
                  No
                </TextBody>
              </Box>

              <ToggleSwitchStateful
                data-testid="register-device-toggle-test-id"
                label=""
                name="registerDevice"
                valueOn="Yes"
                valueOff="No"
                buttonId="registerDeviceToggleId"
                labelId="registerDeviceLabelId"
                mb="md"
                onChange={handleRegisterDeviceToggle}
              />
              <Box mb="md">
                <TextBody tag="p" size="md">
                  Yes
                </TextBody>
              </Box>
            </Box>
          </StyledToggleWrapper>
        </Box>
      )}

      <Button
        allytmln="continueGuestEnrollment-gettingIn"
        text="Continue"
        fullWidthMd
        loading={status === 'Loading'}
        type="submit"
        variant="primary"
      />
    </Form>
  )
}

export const GuestCredentialCreationContent: React.FC<GuestEnrollCredentialCreationProps> = ({
  onComplete,
  status,
  error,
}) => {
  const [errorState, setErrorState] = React.useState(error)

  React.useEffect(() => {
    setErrorState(error)
  }, [error])

  const handleSubmit = (
    {
      username,
      password,
      passwordHint,
      registerDevice,
    }: GuestCredentialCreationFormInterface,
    { setSubmitting }: FormikHelpers<GuestCredentialCreationFormInterface>,
  ): void => {
    setErrorState('')
    onComplete({
      username,
      password,
      passwordHint,
      registerDevice,
    })

    allytm.event('customError', 'errorMessage', 'errorCode', {
      isFormError: !!error, // this will set event102 on the Adobe Analytics beacon
    })

    // We have to set this here as onComplete doesn't return a promise
    // this is required to resubmit after error/failures
    setSubmitting(false)
  }

  const formId = 'guestCredentialCreation'

  const initialValues: GuestCredentialCreationFormInterface = {
    username: '',
    password: '',
    passwordHint: '',
    registerDevice: false,
  }

  return (
    <StyledBox data-testid="guest-credential-creation-page-wrapper">
      <StyledFormWrapper data-testid="guest-credential-creation-form-wrapper">
        <LoginHeading title="Getting in." />
        <StyledTextBody
          tag="p"
          size="sm"
          data-testid="guest-credential-creation-sub-header"
        >
          Make your login info easy for you to remember, but hard for anyone
          else to guess.
        </StyledTextBody>
        <CIAMErrorBanner error={errorState} />
        <Forms
          formId={formId}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          <GuestCredentialForm formId={formId} status={status} />
        </Forms>
      </StyledFormWrapper>

      <StyledSvgWrapper data-testid="getting-in-image">
        <Media.LgUp>
          <GettingInSvg />
        </Media.LgUp>
      </StyledSvgWrapper>
    </StyledBox>
  )
}

export const GuestCredentialCreation: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'GuestCredentialCreation') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <GuestCredentialCreationContent {...activeScreen.payload} />
}
